import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalTypeaheadMenuPlugin } from "@lexical/react/LexicalTypeaheadMenuPlugin";
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  TextNode,
} from "lexical";
import { useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useBasicTypeaheadTriggerMatch } from "../../utils";
import { Row, Space } from "antd";
import styles from "../../index.less";
import useToken from "antd/es/theme/useToken";
import { ReactComponent as VariableIcon } from "../../../../../../assets/variable.svg";
import { EditorVariablePickerType } from "../../../types";

export default function VariablePickerPlugin({
  variables,
}: {
  variables: EditorVariablePickerType[];
}) {
  const [, token] = useToken();

  const [editor] = useLexicalComposerContext();

  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch("{", {
    minLength: 0,
  });

  const { templateVariables, globalVariables, selfVariables } = useMemo(() => {
    const templateVariables: (EditorVariablePickerType & { index: number })[] =
      [];
    const globalVariables: (EditorVariablePickerType & { index: number })[] =
      [];
    const selfVariables: (EditorVariablePickerType & { index: number })[] = [];
    variables.forEach((item, index) => {
      if (item.isTemplateVariable) {
        templateVariables.push({ ...item, index });
      } else if (item.self) {
        selfVariables.push({ ...item, index });
      } else {
        globalVariables.push({ ...item, index });
      }
    });
    return {
      templateVariables,
      globalVariables,
      selfVariables,
    };
  }, [variables]);

  const onSelectOption = useCallback(
    (
      selectedOption: any,
      nodeToRemove: TextNode | null,
      closeMenu: () => void
    ) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || selectedOption == null) {
          return;
        }
        if (nodeToRemove) {
          nodeToRemove.remove();
        }
        selection.insertNodes([$createTextNode(`{${selectedOption.key}}`)]);
        closeMenu();
      });
    },
    [editor]
  );

  return (
    <LexicalTypeaheadMenuPlugin
      onSelectOption={onSelectOption}
      triggerFn={checkForTriggerMatch}
      options={variables as any[]}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) => {
        const nodesRef = useRef<(HTMLDivElement | null)[]>([]);

        // useLayoutEffect(() => {
        //   if (selectedIndex !== null) {
        //     nodesRef.current[selectedIndex]?.scrollIntoView();
        //   }
        // }, [selectedIndex]);

        if (anchorElementRef.current == null) {
          return null;
        }
        return anchorElementRef.current && variables.length
          ? createPortal(
              <div className={styles["picker-container"]}>
                {selfVariables?.length > 0 && (
                  <>
                    <div className={styles["picker-title"]}>节点变量</div>
                    {selfVariables.map((item) => {
                      const { index } = item;
                      return (
                        <Row
                          ref={(node) => (nodesRef.current[index] = node)}
                          align="middle"
                          className={styles["picker-item"]}
                          key={item.key}
                          style={{
                            backgroundColor:
                              selectedIndex === index
                                ? token.colorBgTextHover
                                : "",
                            color:
                              selectedIndex === index
                                ? token.colorPrimaryActive
                                : "",
                          }}
                          onClick={() => {
                            setHighlightedIndex(index);
                            selectOptionAndCleanUp(item);
                          }}
                          onMouseEnter={() => {
                            setHighlightedIndex(index);
                          }}
                        >
                          <Space align="center">
                            <Row align="middle">
                              <VariableIcon
                                width={14}
                                height={14}
                                fill={
                                  selectedIndex === index
                                    ? token.colorPrimaryActive
                                    : ""
                                }
                              />
                            </Row>
                            <div>
                              {item.key}
                              {item.key !== item.label && `(${item.label})`}
                            </div>
                          </Space>
                        </Row>
                      );
                    })}
                  </>
                )}
                {globalVariables?.length > 0 && (
                  <>
                    <div className={styles["picker-title"]}>全局变量</div>
                    {globalVariables.map((item) => {
                      const { index } = item;
                      return (
                        <Row
                          ref={(node) => (nodesRef.current[index] = node)}
                          align="middle"
                          className={styles["picker-item"]}
                          key={item.key}
                          style={{
                            backgroundColor:
                              selectedIndex === index
                                ? token.colorBgTextHover
                                : "",
                            color:
                              selectedIndex === index
                                ? token.colorPrimaryActive
                                : "",
                          }}
                          onClick={() => {
                            setHighlightedIndex(index);
                            selectOptionAndCleanUp(item);
                          }}
                          onMouseEnter={() => {
                            setHighlightedIndex(index);
                          }}
                        >
                          <Space align="center">
                            <Row align="middle">
                              <VariableIcon
                                width={14}
                                height={14}
                                fill={
                                  selectedIndex === index
                                    ? token.colorPrimaryActive
                                    : ""
                                }
                              />
                            </Row>
                            <div>
                              {item.key}
                              {item.key !== item.label && `(${item.label})`}
                            </div>
                          </Space>
                        </Row>
                      );
                    })}
                  </>
                )}
                {templateVariables?.length > 0 && (
                  <>
                    <div className={styles["picker-title"]}>模板变量</div>
                    {templateVariables.map((item) => {
                      const { index } = item;
                      return (
                        <Row
                          ref={(node) => (nodesRef.current[index] = node)}
                          align="middle"
                          className={styles["picker-item"]}
                          key={item.key}
                          style={{
                            backgroundColor:
                              selectedIndex === index
                                ? token.colorBgTextHover
                                : "",
                            color:
                              selectedIndex === index
                                ? token.colorPrimaryActive
                                : "",
                          }}
                          onClick={() => {
                            setHighlightedIndex(index);
                            selectOptionAndCleanUp(item);
                          }}
                          onMouseEnter={() => {
                            setHighlightedIndex(index);
                          }}
                        >
                          <Space align="center">
                            <Row align="middle">
                              <VariableIcon
                                width={14}
                                height={14}
                                fill={
                                  selectedIndex === index
                                    ? token.colorPrimaryActive
                                    : ""
                                }
                              />
                            </Row>
                            <div>
                              {item.key}
                              {item.key !== item.label && `(${item.label})`}
                            </div>
                          </Space>
                        </Row>
                      );
                    })}
                  </>
                )}
              </div>,
              anchorElementRef.current
            )
          : null;
      }}
      onQueryChange={() => {}}
    />
  );
}
